import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FormHelperText, Grid, Typography } from '@material-ui/core';
import { compose } from 'recompose';
import { connect, FormikProps } from 'formik';
import { compact, find, clone, remove, uniq } from 'lodash';
import moment from 'moment';

import { LeaderboardConfig, LeaderboardConfigInput, UserSegment } from 'api';
import { withSegments, IWithSegments } from 'providers/queries';
import { AddSegment, RemoveSegment, ValidatedTextField } from 'components';

import DurationSelector from './DurationSelector';

interface IProps {
  config: LeaderboardConfig;
}

interface IPropsEnhanced extends IProps, IWithSegments {
  formik: FormikProps<LeaderboardConfigInput>;
}

const enhancers = compose<IPropsEnhanced, IProps>(connect, withSegments());

const Edit: FC<IPropsEnhanced> = props => {
  const { t } = useTranslation();

  const { config, formik, segments } = props;
  const { all_users } = config;
  const { segment_ids } = formik.values;

  function getActiveSegments() {
    return compact(
      segment_ids.reduce((list, id) => {
        const segment = find(segments, ['id', id]);
        return list.concat([segment]);
      }, []),
    );
  }

  function handleSave(attribute: string, time: number) {
    const { setFieldValue } = formik;
    const momentValue = moment.unix(time);

    if (!time) {
      return setFieldValue(attribute, null);
    }

    if (attribute === 'start_at') {
      momentValue.startOf('d');
    } else if (attribute === 'end_at') {
      momentValue.endOf('d');
    }

    setFieldValue(attribute, momentValue.unix());
  }

  function handleAdd(segment: UserSegment) {
    const { setFieldValue, values } = formik;
    const segmentIds = clone(values.segment_ids);

    segmentIds.splice(0, 0, segment.id);
    setFieldValue('segment_ids', uniq(segmentIds));
  }

  function handleRemove(segment: UserSegment) {
    const { setFieldValue, values } = formik;
    const segmentIds = clone(values.segment_ids);

    remove(segmentIds, segmentId => segmentId === segment.id);
    setFieldValue('segment_ids', segmentIds);
  }

  function renderSegments() {
    const activeSegments = getActiveSegments();

    return activeSegments.map(segment => (
      <RemoveSegment
        canRemove
        key={segment.id}
        segment={segment}
        onRemove={segmentToRemove => handleRemove(segmentToRemove)}
        removeKey="admin:leaderboardsRemoveSegment"
        confirmKey="common:delete"
      />
    ));
  }

  function renderStartDate() {
    const { values } = formik;
    const { start_at } = values;

    const hasStartDate = !!start_at;
    const startDateFormatted = moment.unix(start_at).format('ll');
    const startDateLabel = t('admin:startDate');
    const startAtLabel = hasStartDate ? startDateFormatted : startDateLabel;

    return (
      <DurationSelector
        inputLabel={startDateLabel}
        buttonLabel={startAtLabel}
        value={start_at}
        onSave={time => handleSave('start_at', time)}
      />
    );
  }

  function renderEndDate() {
    const { values } = formik;
    const { end_at } = values;

    const hasEndDate = !!end_at;
    const endDateFormatted = moment.unix(end_at).format('ll');
    const endDateLabel = t('admin:endDate');
    const endAtLabel = hasEndDate ? endDateFormatted : endDateLabel;

    return (
      <DurationSelector
        inputLabel={endDateLabel}
        buttonLabel={endAtLabel}
        value={end_at}
        onSave={time => handleSave('end_at', time)}
      />
    );
  }

  function renderDuration() {
    return (
      <Grid container spacing={3}>
        <Grid item>{renderStartDate()}</Grid>
        <Grid item>{renderEndDate()}</Grid>
      </Grid>
    );
  }

  function renderDetail() {
    if (all_users) {
      return null;
    }

    const { setFieldError } = formik;

    const segmentIdsError = formik.errors.segment_ids;
    const endAtError = formik.errors.end_at;

    // Account for post creation state where no segments are defined
    if (!segmentIdsError && !segment_ids.length) {
      setFieldError('segment_ids', t('admin:leaderboardsSegmentRequired'));
    }

    return (
      <Fragment>
        <Grid item>
          <Grid container>
            <Grid item xs={2}>
              <Typography variant="subtitle2">{t('common:segment_plural')}</Typography>
            </Grid>
            <Grid item xs={8}>
              <AddSegment
                segments={segments}
                onChange={segment => handleAdd(segment)}
                exclusions={segment_ids}
                TextFieldProps={{
                  label: null,
                  fullWidth: true,
                  error: !!segmentIdsError,
                  helperText: segmentIdsError,
                }}
              />
              {renderSegments()}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container>
            <Grid item xs={2}>
              <Typography variant="subtitle2">{t('common:duration')}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography color="textSecondary" gutterBottom>
                <em>{t('admin:leaderboardsDurationTooltip')}</em>
              </Typography>
              {renderDuration()}
              <FormHelperText error>{endAtError}</FormHelperText>
            </Grid>
          </Grid>
        </Grid>
      </Fragment>
    );
  }

  return (
    <Grid container direction="column" wrap="nowrap" spacing={5}>
      <Grid item>
        <Grid container>
          <Grid item xs={2}>
            <Typography variant="subtitle2">{t('common:description')}</Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography color="textSecondary">
              <em>{t('admin:leaderboardsDescriptionToolTip')}</em>
            </Typography>
            <ValidatedTextField
              color="primary"
              TextFieldProps={{ multiline: true, rows: 4 }}
              validationType="change"
              name="description"
              i18nKey="admin:leaderboardsDescription"
            />
          </Grid>
        </Grid>
      </Grid>
      {renderDetail()}
    </Grid>
  );
};

export default enhancers(Edit);
